import { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, message, Space } from 'antd';
import { Staff, StaffRole, StaffStatus, CreateStaffRequest, UpdateStaffRequest } from '@mygate/common';
import { staffService } from '@/services/staff';

const { Option } = Select;

interface AddEditStaffModalProps {
  visible: boolean;
  staff: Staff | null;
  isSelfEdit?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddEditStaffModal({ visible, staff, isSelfEdit, onClose, onSuccess }: AddEditStaffModalProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      if (staff) {
        // Editing existing staff
        form.setFieldsValue({
          phoneNumber: staff.phoneNumber,
          firstName: staff.firstName,
          lastName: staff.lastName,
          email: staff.email,
          role: staff.role,
          status: staff.status,
        });
      } else {
        // Adding new staff
        form.resetFields();
        form.setFieldsValue({
          role: StaffRole.SUPPORT,
          status: StaffStatus.ACTIVE,
        });
      }
    }
  }, [visible, staff, form]);

  const handleSubmit = async (values: CreateStaffRequest & { status?: StaffStatus }) => {
    setLoading(true);
    try {
      if (staff) {
        // Update existing staff
        const updateData: UpdateStaffRequest = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          role: values.role,
          status: values.status,
        };
        await staffService.updateStaff(staff.id, updateData);
        message.success('Staff member updated successfully');
      } else {
        // Create new staff
        const createData: CreateStaffRequest = {
          phoneNumber: values.phoneNumber,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          role: values.role,
        };
        await staffService.createStaff(createData);
        message.success('Staff member created successfully');
      }
      onSuccess();
    } catch (error) {
      message.error((error as Error).message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={staff ? (isSelfEdit ? 'Edit Your Profile' : 'Edit Staff Member') : 'Add Staff Member'}
      open={visible}
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={500}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark="optional">
        <Form.Item
          name="phoneNumber"
          label="Phone Number"
          rules={[
            { required: true, message: 'Please enter phone number' },
            { pattern: /^\+?[1-9]\d{7,14}$/, message: 'Please enter a valid phone number' },
          ]}
        >
          <Input placeholder="+48XXXXXXXXX" disabled={!!staff} />
        </Form.Item>

        <Space style={{ width: '100%' }} size="middle">
          <Form.Item
            name="firstName"
            label="First Name"
            rules={[{ required: true, message: 'Please enter first name' }]}
            style={{ flex: 1 }}
          >
            <Input placeholder="John" />
          </Form.Item>

          <Form.Item
            name="lastName"
            label="Last Name"
            rules={[{ required: true, message: 'Please enter last name' }]}
            style={{ flex: 1 }}
          >
            <Input placeholder="Doe" />
          </Form.Item>
        </Space>

        <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Please enter a valid email' }]}>
          <Input placeholder="john.doe@example.com" />
        </Form.Item>

        <Space style={{ width: '100%' }} size="middle">
          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select a role' }]}
            style={{ flex: 1 }}
          >
            <Select disabled={isSelfEdit}>
              <Option value={StaffRole.ADMIN}>Admin</Option>
              <Option value={StaffRole.SUPPORT}>Support</Option>
            </Select>
          </Form.Item>

          {staff && (
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: 'Please select status' }]}
              style={{ flex: 1 }}
            >
              <Select disabled={isSelfEdit}>
                <Option value={StaffStatus.ACTIVE}>Active</Option>
                <Option value={StaffStatus.SUSPENDED}>Suspended</Option>
              </Select>
            </Form.Item>
          )}
        </Space>
      </Form>
    </Modal>
  );
}
